import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserInfo } from '../../../models/user-info.model';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import { FormControl } from '@angular/forms';
import {
  Observable,
  Subscription,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { Router } from '@angular/router';
import { UsersService } from 'src/app/services/users/users.service';
import { SharedServiceMailBox } from 'src/app/services/sharemailbox/sharemailbox.service';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { MyUsers } from 'src/app/models/my-users.model';

@Component({
  selector: 'app-adduser-dialog',
  templateUrl: './adduser-dialog.html',
  styleUrls: ['./adduser-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddUserDialogComponent implements OnInit, OnDestroy {
  isLoading = false;
  error: Error = { error: { message: '', code: '' } };
  users: MyUsers[] = [];
  selectedUsers: UserInfo[] = [];

  currentUser: UserInfo;
  selectedEtablissement: string;
  hasMultiEtab = false;
  currentEtab: string;

  private subscription: Subscription;
  booleanValue = false;

  // Pagination
  loadedUsers = 1;
  private allowedPageSize: number[] = [5];
  selectedPageSize: number = this.allowedPageSize[0];
  currentPage = 0; //0 based page index
  hasMoreUsers = false;
  private contextStorageUsersAdd: {
    pageIndex: number;
    pageSize: number;
    loadedUsers: number;
    lastNameFilter: string;
    firstNameFilter: string;
    hasMoreUsers: boolean;
  };

  lastNameSearchField: FormControl = new FormControl('');
  firstNameSearchField: FormControl = new FormControl('');

  lastNameSearchSub: Subscription;
  firstNameSearchSub: Subscription;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  dataSource = new MatTableDataSource<MyUsers>([]);
  selection = new SelectionModel<MyUsers>(true, []);

  displayedColumns: string[] = [];
  expandedElement: MyUsers | null;
  windowWidth: number = window.innerWidth;
  sortedValue: string;

  alreadyAddedUsersMail: string[];
  isSingleUser: boolean;

  constructor(
    public matDialogRef: MatDialogRef<AddUserDialogComponent>,
    private _userService: UsersService,
    private _router: Router,
    private _sharedService: SharedServiceMailBox,
    private _localStorageService: LocalStorageService,
    @Inject(MAT_DIALOG_DATA)
    public data: { _users: string[]; singleUser: boolean }
  ) {
    this.alreadyAddedUsersMail = data._users;
    this.isSingleUser = data.singleUser;
  }

  ngOnDestroy(): void {
    this.firstNameSearchSub.unsubscribe();
    this.lastNameSearchSub.unsubscribe();
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.onComponentInit();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateDisplayedColumns();
    this.windowWidth = window.innerWidth;
  }

  private updateDisplayedColumns(): void {
    if (window.innerWidth >= 1000) {
      this.displayedColumns = [
        'select',
        'firstName',
        'lastName',
        'officialemail',
      ];
    } else if (window.innerWidth < 1000 && window.innerWidth >= 700) {
      this.displayedColumns = ['select', 'firstName', 'lastName', 'expand'];
    } else {
      this.displayedColumns = ['select', 'firstName', 'expand'];
    }
  }

  onPreviousPageclick() {
    this.currentPage--;
    this.getMyEstablishementUsers();
  }

  onNextPageClick() {
    this.currentPage++;
    this.getMyEstablishementUsers();
  }

  /**
   * Initialize current page and users list.
   * Forces an API call on the next pagnination event.
   */
  private resetValues() {
    this.currentPage = 0;
    this.loadedUsers = 0;
    this.users = [];
    this.dataSource.data = this.users;
    this.selection.clear();
    this._localStorageService.removeItem('UsersAdd');
  }

  /**
   * Reset the content of the 3 search fields.
   */
  private resetFields() {
    this.firstNameSearchField.reset('', { emitEvent: false });
    this.lastNameSearchField.reset('', { emitEvent: false });
    this.setDataSource();
  }

  /**
   * Returns the one-based numbering representation of this.currentPage.
   */
  public get currentPageForView(): number {
    return this.currentPage + 1;
  }

  onComponentInit() {
    this.updateDisplayedColumns();
    this.currentUser = this._userService.currentUserValue;
    if (this.currentUser == null) {
      this._router.navigate(['/login']);
    }

    this.subscription = this._sharedService.currentBoolean.subscribe(value => {
      this.booleanValue = value;
    });

    if (this.booleanValue) {
      // reset fields when etab changed
      this.resetFields();
    }

    if (
      this._localStorageService.getItem<string>('userDefaultGE') &&
      this.currentUser.groupEtablissement.includes(
        this._localStorageService.getItem<string>('userDefaultGE')
      )
    ) {
      this.currentEtab =
        this._localStorageService.getItem<string>('userDefaultGE');
    } else {
      this.currentEtab = this.currentUser.groupEtablissement[0];
    }

    if (this.currentUser.groupEtablissement.length > 1) {
      this.hasMultiEtab = true;
    }

    //Save and load the context manually.
    const context = this._localStorageService.getItem<
      typeof this.contextStorageUsersAdd
    >('contextStorageUsersAdd');

    if (context != null) {
      this.contextStorageUsersAdd = {
        firstNameFilter: context.firstNameFilter,
        lastNameFilter: context.lastNameFilter,
        loadedUsers: context.loadedUsers,
        pageIndex: context.pageIndex,
        pageSize: context.pageSize,
        hasMoreUsers: context.hasMoreUsers,
      };
      this.selectedPageSize = this.contextStorageUsersAdd.pageSize;
      this.loadedUsers = this.contextStorageUsersAdd.loadedUsers;
      this.currentPage = this.contextStorageUsersAdd.pageIndex;
      this.hasMoreUsers = this.contextStorageUsersAdd.hasMoreUsers;
      this.firstNameSearchField.setValue(
        this.contextStorageUsersAdd.firstNameFilter,
        {
          emitEvent: false,
        }
      );
      this.lastNameSearchField.setValue(
        this.contextStorageUsersAdd.lastNameFilter,
        {
          emitEvent: false,
        }
      );
      this.getMyEstablishementUsers();
    } else if (!this.hasMultiEtab) {
      //Done separately to retain functionality but without interfering with the previous loading
      this.getMyEstablishementUsers();
    } else if (context === null) {
      // Display default first etab in Grid
      this.resetValues();
      this.getMyEstablishementUsers();
    }
    this.firstNameSearchSub = this.firstNameSearchField.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((data: string) => {
        let fieldValue = data.toLowerCase();
        fieldValue = fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1);
        this.firstNameSearchField.setValue(fieldValue, {
          emitEvent: false,
        });
        this.resetValues();
        this.getMyEstablishementUsers();
      });
    this.lastNameSearchSub = this.lastNameSearchField.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((data: string) => {
        let fieldValue = data.toLowerCase();
        fieldValue = fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1);
        this.lastNameSearchField.setValue(fieldValue, {
          emitEvent: false,
        });
        this.resetValues();
        this.getMyEstablishementUsers();
      });
  }

  private getMyEstablishementUsers(): Observable<UserInfo[]> {
    //Hit the cache first
    this.isLoading = true;
    const usersFromCache: MyUsers[] =
      this._localStorageService.getItem<MyUsers[]>('UsersAdd') || [];

    if (usersFromCache.length > 0) {
      const start = this.currentPage * this.selectedPageSize;
      const end = (this.currentPage + 1) * this.selectedPageSize;

      const paginatedResult = usersFromCache.slice(start, end);
      if (paginatedResult.length > 0) {
        this.isLoading = false;
        this.users = paginatedResult;
        this.dataSource.data = this.users;
        this.selection.clear();
        this.setDataSource();
        return;
      }
    }
    this._userService
      .getAllmyUsersForMailbox(
        this.currentEtab,
        this.currentPage * this.selectedPageSize,
        this.selectedPageSize + 1, //Get one more for 'hasMoreUsers'
        this.firstNameSearchField.value,
        this.lastNameSearchField.value
      )
      .subscribe({
        next: result => {
          this.hasMoreUsers = result.length > this.selectedPageSize;
          this.users = result.slice(0, this.selectedPageSize); //Removes the additional user

          this.dataSource.data = this.users;
          this.selection.clear();
          if (usersFromCache.length > 0) {
            this._localStorageService.setItem(
              'UsersAdd',
              usersFromCache.concat(this.users)
            );
            this.loadedUsers = usersFromCache.length + this.users.length;
            this.isLoading = false;
          } else {
            this._localStorageService.setItem('UsersAdd', this.users);
            this.isLoading = false;
            this.loadedUsers = this.users.length;
          }
          this.setDataSource();
        },
      });
  }

  getSelectedUsers() {
    this.selection.selected.map(user => {
      const temp = new UserInfo();
      temp.officialemail = user.officialEmail;
      this.selectedUsers.push(temp);
    });

    this._localStorageService.removeItem('UsersAdd');
    this._localStorageService.removeItem('contextStorageUsersAdd');

    this.matDialogRef.close(this.selectedUsers);
  }

  private setDataSource() {
    this.contextStorageUsersAdd = {
      firstNameFilter: this.firstNameSearchField.value,
      lastNameFilter: this.lastNameSearchField.value,
      loadedUsers: this.loadedUsers,
      pageIndex: this.currentPage,
      pageSize: this.selectedPageSize,
      hasMoreUsers: this.hasMoreUsers,
    };

    this._localStorageService.setItem(
      'contextStorageUsersAdd',
      this.contextStorageUsersAdd
    );
  }

  isSelectDisabled(userEmail: string) {
    if (
      this.isSingleUser &&
      this.alreadyAddedUsersMail.length + this.selection.selected.length >= 1 &&
      !this.selection.selected.some(
        selected => userEmail === selected.officialEmail
      )
    ) {
      return true;
    }
    return this.alreadyAddedUsersMail.some(
      alreadyAddedUser => userEmail === alreadyAddedUser
    );
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const rowsNotAdded = this.dataSource.data.filter(
      user =>
        !this.alreadyAddedUsersMail.some(email => email === user.officialEmail)
    );
    const numRows = rowsNotAdded.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(
      ...this.dataSource.data.filter(
        user =>
          !this.alreadyAddedUsersMail.some(
            email => email === user.officialEmail
          )
      )
    );
  }

  checkboxLabel(row?: MyUsers): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} ${row.firstName + row.lastName}`;
  }

  sortData(sort: Sort) {
    this.sortedValue = sort.active;
    if (!sort.active || sort.direction === '') {
      this.sortedValue = null;
      return;
    }

    this.dataSource.data = this.dataSource.data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'firstName':
          return this.compare(
            a.firstName.toLocaleLowerCase(),
            b.firstName.toLocaleLowerCase(),
            isAsc
          );
        case 'lastName':
          return this.compare(
            a.lastName.toLocaleLowerCase(),
            b.lastName.toLocaleLowerCase(),
            isAsc
          );
      }
    });
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  toggleRow(element: MyUsers): void {
    this.expandedElement = this.expandedElement === element ? null : element;
  }
}
